import type { PlainQuoteRequestForm as PlainQuoteRequestFormData } from '@repo/api-contracts';
import { type FormEvent, type ReactElement, useId, useState } from 'react';
import {
  earliestDesiredDate,
  QUOTE_REQUEST_TEXT_LIMITS,
  SizeRemainingSummary,
  SizeTotalFields,
} from '@/entities/quote';
import { Button, cn, FormField, FormSection, Label } from '@/shared/ui';
import type {
  MemberDestinationValues,
  ShippingAddressValues,
} from '../model/quote-request-submission';
import { useQuoteRequestDestinations } from '../model/use-quote-request-destinations';
import { useSubmitPlainQuoteRequest } from '../model/use-submit-plain-quote-request';
import { DestinationAddButton } from './destination-add-button';
import { EstimateSummary } from './estimate-summary';
import { MemberDestinationCard } from './member-destination-card';
import { ProductSummary } from './product-summary';
import { TotalQuantity } from './total-quantity';

export interface PlainQuoteRequestFormProps {
  readonly form: PlainQuoteRequestFormData;
  /** 상품 대표 사진 주소 — 폼 계약에 없어 페이지가 공개 상품 상세 조회로 읽어 넘긴다. null 이면 "상품 사진 없음". */
  readonly productImageUrl: string | null;
  /** 제출에 성공하면 견적 요청 id 로 부른다 — 페이지가 내 견적 상세로 보낸다. */
  readonly onSubmitted: (quoteRequestId: string) => void;
  /** 처음 수량(상품 상세에서 고른 수량) — 없거나 최소 주문 수량 미만이면 최소 주문 수량으로 시작한다. */
  readonly initialQuantity?: number | null;
}

const EMPTY_ADDRESS: ShippingAddressValues = {
  recipientName: '',
  recipientPhone: '',
  postalCode: '',
  address1: '',
  address2: '',
};

const {
  memo: MEMO_MAX_LENGTH,
  companyName: COMPANY_NAME_MAX_LENGTH,
  contactName: CONTACT_NAME_MAX_LENGTH,
} = QUOTE_REQUEST_TEXT_LIMITS;

/** 추가한 카드는 빈 칸으로 시작한다 — 첫 카드만 최소 주문 수량(spec §6-1). */
const blankDestination = (): MemberDestinationValues => ({
  ...EMPTY_ADDRESS,
  quantity: '',
  sizes: [],
});

/** 칸 높이 44px — 휴대폰에서도 누르기 쉬운 크기. */
const INPUT_CLASS = 'h-11';

/**
 * 일반 견적 요청 폼(일반 견적 설계 §5) — 편집영역 없는 상품을 디자인·인쇄 방식 없이 요청한다. 레이아웃은 `QuoteRequestForm` 과 같다.
 * 왼쪽: 01 납기(희망 납기일만) → 02 배송지·수량 → 03 요청 정보. 오른쪽: 디자인 정보 대신 상품 정보(대표 사진·상품·색상)와
 * 따라 내려오는(sticky) 임시 견적·요청 버튼. 좁은 화면에서는 상품 정보 → 입력 묶음 → 임시 견적·버튼 순으로 쌓인다.
 */
export const PlainQuoteRequestForm = ({
  form,
  productImageUrl,
  onSubmitted,
  initialQuantity,
}: PlainQuoteRequestFormProps): ReactElement => {
  const destinations = useQuoteRequestDestinations<MemberDestinationValues>({
    sizeOptions: form.sizeOptions,
    minOrderQty: form.minOrderQty,
    initialQuantity,
    blankDestination,
  });
  const [desiredDate, setDesiredDate] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState(form.defaultContactName);
  const [memo, setMemo] = useState('');
  const memoId = useId();
  const memoErrorId = `${memoId}-error`;
  const { fieldErrors, destinationErrors, formError, isPending, submit } =
    useSubmitPlainQuoteRequest({
      minOrderQty: form.minOrderQty,
      priceTiers: form.priceTiers,
      shippingFeeSettings: form.shippingFeeSettings,
      sizeOptions: form.sizeOptions,
    });
  const { hasSizes, sizeFields, totalQuantity, showSizeRows } = destinations;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const quoteRequestId = await submit({
      productId: form.productId,
      colorOptionId: form.colorOptionId,
      desiredDate,
      sizeFields,
      destinations: destinations.toSubmissionDestinations(),
      memo,
      companyName,
      contactName,
    });

    if (quoteRequestId !== null) {
      onSubmitted(quoteRequestId);
    }
  };

  return (
    <form
      aria-label="견적 요청"
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      className="grid gap-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:grid-rows-[auto_1fr] lg:gap-x-12 lg:gap-y-10 xl:gap-x-16"
      noValidate
    >
      <ProductSummary
        imageUrl={productImageUrl}
        productName={form.productName}
        colorName={form.colorName}
        className="lg:col-start-2 lg:row-start-1"
      />

      <div className="flex min-w-0 flex-col gap-12 lg:col-start-1 lg:row-span-2 lg:row-start-1">
        <FormSection order={1} title="납기">
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              label="희망 납기일"
              type="date"
              description="선택 사항 — 내일 이후 날짜만 받습니다."
              min={earliestDesiredDate(new Date())}
              className={INPUT_CLASS}
              value={desiredDate}
              error={fieldErrors.desiredDate}
              onChange={(event) => setDesiredDate(event.target.value)}
            />
          </div>
        </FormSection>

        <FormSection order={2} title="배송지·수량">
          <div className="flex flex-col gap-8">
            <p className="text-sm text-muted-foreground">
              {hasSizes
                ? '사이즈별 수량을 적고, 배송지가 둘 이상이면 배송지마다 사이즈를 나눠 주세요.'
                : '배송지마다 수량을 나눠 주세요. 배송비는 배송지마다 붙어요.'}
            </p>

            {hasSizes ? (
              <SizeTotalFields
                options={form.sizeOptions}
                values={sizeFields}
                error={fieldErrors.quantity}
                onChange={destinations.changeSizeField}
              />
            ) : null}

            <TotalQuantity
              total={totalQuantity}
              minOrderQty={form.minOrderQty}
              error={hasSizes ? undefined : fieldErrors.quantity}
            />

            {destinations.items.map((item, index) => (
              <MemberDestinationCard
                key={item.key}
                order={index + 1}
                value={item.value}
                errors={destinationErrors[item.key] ?? {}}
                canRemove={destinations.canRemove}
                sizeOptions={form.sizeOptions}
                showQuantity={!hasSizes}
                showSizeRows={showSizeRows}
                onChange={(patch) => destinations.update(item.key, patch)}
                onRemove={() => destinations.remove(item.key)}
              />
            ))}

            {showSizeRows ? <SizeRemainingSummary remaining={destinations.remainingSizes} /> : null}

            {fieldErrors.destinations ? (
              <p role="alert" className="text-sm text-destructive">
                {fieldErrors.destinations}
              </p>
            ) : null}

            <DestinationAddButton count={destinations.items.length} onAdd={destinations.add} />
          </div>
        </FormSection>

        <FormSection order={3} title="요청 정보">
          <div className="grid gap-5 sm:grid-cols-2">
            <FormField
              label="회사·단체명"
              autoComplete="organization"
              description={`선택 사항, ${COMPANY_NAME_MAX_LENGTH}자 이하`}
              maxLength={COMPANY_NAME_MAX_LENGTH}
              className={INPUT_CLASS}
              value={companyName}
              error={fieldErrors.companyName}
              onChange={(event) => setCompanyName(event.target.value)}
            />

            <FormField
              label="담당자명"
              autoComplete="name"
              isRequired
              required
              maxLength={CONTACT_NAME_MAX_LENGTH}
              className={INPUT_CLASS}
              value={contactName}
              error={fieldErrors.contactName}
              onChange={(event) => setContactName(event.target.value)}
            />

            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor={memoId}>요청 메모</Label>
              <textarea
                id={memoId}
                rows={5}
                maxLength={MEMO_MAX_LENGTH}
                value={memo}
                aria-invalid={fieldErrors.memo ? true : undefined}
                aria-describedby={fieldErrors.memo ? memoErrorId : undefined}
                className={cn(
                  'min-h-32 w-full resize-y rounded-2xl border border-input bg-background px-4 py-3 text-sm leading-relaxed transition-colors',
                  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  fieldErrors.memo && 'border-destructive focus-visible:ring-destructive',
                )}
                placeholder={`선택 사항, ${MEMO_MAX_LENGTH.toLocaleString('ko-KR')}자 이하`}
                onChange={(event) => setMemo(event.target.value)}
              />
              {fieldErrors.memo ? (
                <p id={memoErrorId} role="alert" className="text-xs text-destructive">
                  {fieldErrors.memo}
                </p>
              ) : null}
            </div>
          </div>
        </FormSection>
      </div>

      <div className="flex flex-col gap-5 lg:sticky lg:top-24 lg:col-start-2 lg:row-start-2 lg:self-start">
        <EstimateSummary
          tiers={form.priceTiers}
          minOrderQty={form.minOrderQty}
          totalQuantity={totalQuantity}
          postalCodes={destinations.items.map((item) => item.value.postalCode)}
          shippingFeeSettings={form.shippingFeeSettings}
        />

        {formError ? (
          <p
            role="alert"
            className="border-l-2 border-destructive bg-destructive/5 px-5 py-4 text-sm text-destructive"
          >
            {formError}
          </p>
        ) : null}

        <Button type="submit" size="lg" isLoading={isPending} className="h-14 w-full text-base">
          견적 요청
        </Button>
        <p className="text-xs leading-relaxed text-muted-foreground">
          임시 견적이며 확정 견적은 운영자가 검토 후 발행합니다.
        </p>
      </div>
    </form>
  );
};
